import { useEffect, useState } from 'react';
import { Navigate, useParams, useLocation, Link } from 'react-router-dom';
import { productPriceLabel } from '../data/products';
import { useSite } from '../context/SiteContext';
import {
  getCategoryMeta,
  buildBreadcrumbJsonLd,
  buildItemListJsonLd,
} from '../config/seo';
import * as publicApi from '../api/publicClient';
import OptimizedImage from '../components/OptimizedImage';
import SeoHead from '../components/SeoHead';
import './CategoryListing.css';

function ProductThumb({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div
        className="product-card__thumb product-card__thumb--placeholder"
        aria-hidden
      />
    );
  }
  return (
    <OptimizedImage
      src={src}
      alt={alt}
      className="product-card__img"
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

export default function CategoryListing() {
  const { slug } = useParams();
  const { getWhatsAppUrl } = useSite();
  const { pathname } = useLocation();
  const isSekerlik = pathname.startsWith('/sekerlik');
  const hubPath = isSekerlik ? '/sekerlik' : '/davetiyeler';
  const hubKey = isSekerlik ? 'sekerlik' : 'davetiyeler';

  const [category, setCategory] = useState(null);
  const [siblingCategories, setSiblingCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setNotFound(false);
      try {
        const categories = await publicApi.getCategories(hubKey);
        if (cancelled) return;
        const match = categories.find((c) => c.slug === slug);
        if (!match) {
          setNotFound(true);
          return;
        }
        setCategory(match);
        setSiblingCategories(categories);
        const items = await publicApi.getProducts(hubKey, slug);
        if (!cancelled) setProducts(items);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [hubKey, slug]);

  if (loading) {
    return (
      <main className="page listing-page">
        <p className="section-note" style={{ padding: '2rem 1rem' }}>Yükleniyor…</p>
      </main>
    );
  }

  if (notFound || !category) {
    return <Navigate to={hubPath} replace />;
  }

  const whatsappHref = getWhatsAppUrl();
  const hasProducts = products.length > 0;
  const path = `${hubPath}/${slug}`;
  const meta = getCategoryMeta(hubKey, slug, category);
  const hubLabel = isSekerlik ? 'Şekerlik' : 'Davetiyeler';
  const jsonLd = [
    buildBreadcrumbJsonLd([
      { name: 'Anasayfa', path: '/' },
      { name: hubLabel, path: hubPath },
      { name: category.shortTitle || category.title, path },
    ]),
    hasProducts
      ? buildItemListJsonLd(
          category.title,
          products.map((p) => ({
            name: p.name,
            path: `${path}/${p.id}`,
          }))
        )
      : null,
  ];

  return (
    <main className="page listing-page">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path={path}
        jsonLd={jsonLd}
      />
      <nav className="breadcrumb" aria-label="Sayfa konumu">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden> / </span>
        <Link to={hubPath}>{isSekerlik ? 'Şekerlik' : 'Davetiyeler'}</Link>
        <span aria-hidden> / </span>
        <span>{category.shortTitle}</span>
      </nav>

      <header className="listing-hero">
        <h1 className="listing-hero__title">{category.title}</h1>
        <p className="listing-hero__desc">{category.description}</p>
      </header>

      <section className="product-grid section" aria-labelledby="products-heading">
        <h2 id="products-heading" className="section-title">
          Ürünler
        </h2>
        {hasProducts ? (
          <>
            <p className="section-note">
              Fiyatlar yönlendiricidir; güncel teyit ve sipariş için WhatsApp ile
              iletişime geçebilirsiniz.
            </p>
            <ul className="product-grid__list">
              {products.map((p) => {
                const priceText = productPriceLabel(p);
                const waMsg = `Merhaba, "${p.name}" (${category.title}) hakkında bilgi almak istiyorum.`;
                const productWhatsApp = whatsappHref
                  ? getWhatsAppUrl(waMsg)
                  : null;
                const detailTo = `${hubPath}/${slug}/${p.id}`;
                return (
                  <li key={p.id} className="product-card">
                    <Link
                      to={detailTo}
                      className="product-card__main-link"
                    >
                      <div className="product-card__thumb-wrap">
                        <ProductThumb src={p.image} alt={p.name} />
                      </div>
                      <div className="product-card__info">
                        <h3 className="product-card__name">{p.name}</h3>
                        {p.hint ? (
                          <p className="product-card__hint">{p.hint}</p>
                        ) : null}
                        <div className="product-card__meta">
                          {priceText ? (
                            <p className="product-card__price">{priceText}</p>
                          ) : (
                            <p className="product-card__price product-card__price--muted">
                              Fiyat için iletişime geçin
                            </p>
                          )}
                          {p.priceNote ? (
                            <p className="product-card__price-note">{p.priceNote}</p>
                          ) : null}
                        </div>
                      </div>
                    </Link>
                    {productWhatsApp ? (
                      <div className="product-card__cta-wrap">
                        <a
                          href={productWhatsApp}
                          className="product-card__cta"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Bilgi / sipariş
                        </a>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <div className="listing-empty">
            <p className="listing-empty__text">
              Bu kategori için henüz vitrin ürünü eklenmedi.
            </p>
            {whatsappHref ? (
              <a
                href={getWhatsAppUrl(
                  `Merhaba, ${category.title} için ürün ve fiyat bilgisi almak istiyorum.`
                )}
                className="listing-empty__btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp ile yazın
              </a>
            ) : null}
          </div>
        )}
      </section>

      <section className="sibling-nav section">
        <h2 className="section-title">Diğer kategoriler</h2>
        <ul className="sibling-nav__list">
          {siblingCategories
            .filter((c) => c.slug !== slug)
            .map((c) => (
              <li key={c.slug}>
                <Link to={`${hubPath}/${c.slug}`}>{c.title}</Link>
              </li>
            ))}
        </ul>
      </section>
    </main>
  );
}
